import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHeart, FiTarget, FiUsers, FiTrendingUp, FiShield, FiStar } = FiIcons;

function About() {
  const coreValues = [
    {
      icon: FiHeart,
      title: "Mänsklighet först",
      description: "De bästa resultaten kommer när människor får vara sig själva på jobbet."
    },
    {
      icon: FiShield,
      title: "Psykologisk trygghet",
      description: "En grund där alla känner sig säkra att uttrycka sig, ställa frågor och göra misstag."
    },
    {
      icon: FiUsers,
      title: "Gemenskap och tillit",
      description: "Starka relationer och ömsesidig respekt är nyckeln till framgångsrika team."
    },
    {
      icon: FiTarget,
      title: "Balans och välmående",
      description: "Hållbar prestation kräver att vi respekterar gränser och prioriterar återhämtning."
    },
    {
      icon: FiStar,
      title: "Individuell autonomi",
      description: "Varje person är unik och bör få frihet att arbeta på sitt eget optimala sätt."
    },
    {
      icon: FiTrendingUp,
      title: "Kontinuerlig utveckling",
      description: "En kultur av lärande där vi ständigt utvecklas tillsammans."
    }
  ];

  return (
    <div className="py-12">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Om <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">Okej-kulturen</span>
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              För att skapa arbetsplatser där människor kan vara precis det - människor. 
              Där sårbarhet är styrka, gemenskap är kraft, och balans är nyckeln till framgång.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Historien bakom kulturen
            </h2>
            
            <div className="prose prose-lg mx-auto text-gray-700">
              <p>
                Okej-kulturen föddes ur en enkel insikt: de mest framgångsrika teamen är inte de som 
                aldrig gör misstag eller som arbetar mest, utan de där människor känner sig trygga 
                nog att vara autentiska, sårbara och mänskliga.
              </p>
              
              <p>
                I en värld som ständigt förändras och där kunskap fördubblas i exponentiell takt, 
                är det lätt att känna pressen att alltid ha svaret, att alltid vara uppdaterad, 
                att aldrig visa svaghet. Men vad händer om vi istället omfamnar osäkerheten, 
                ställer de 'dumma' frågorna och ser sårbarhet som en styrka?
              </p>
              
              <p>
                Forskning inom psykologisk trygghet, innovation och teamdynamik visar att de 
                arbetsplatser som presterar bäst är de som tillåter människor att vara just 
                människor - med alla våra styrkor, svagheter och allt däremellan.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Våra grundläggande värderingar
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Dessa värderingar genomsyrar allt vi gör och är grunden för att skapa 
              en verkligt human arbetsplats.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mb-4">
                  <SafeIcon icon={value.icon} className="text-white text-xl" />
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {value.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Vår filosofi
            </h2>
            
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <blockquote className="text-xl text-gray-700 italic leading-relaxed mb-6">
                "Det är okej att säga 'jag vet inte', att be om hjälp, att sätta gränser och att vara sig själv. 
                Det är i dessa enkla men djupt betydelsefulla beteenden som den verkliga magin uppstår."
              </blockquote>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiHeart} className="text-white text-2xl" />
                </div>
                <p className="text-gray-600 font-medium">Jonas Jaani</p>
                <p className="text-gray-500 text-sm">Skapare av Okej-kulturen</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Påverkan och resultat
            </h2>
            <p className="text-lg text-gray-600">
              När vi implementerar Okej-kulturen ser vi konkreta förbättringar på flera områden.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center p-6 bg-white rounded-2xl shadow-lg"
            >
              <div className="text-4xl font-bold text-blue-600 mb-2">85%</div>
              <div className="text-gray-700 font-medium mb-2">Ökat engagemang</div>
              <div className="text-sm text-gray-600">Medarbetare känner sig mer engagerade när de kan vara sig själva</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center p-6 bg-white rounded-2xl shadow-lg"
            >
              <div className="text-4xl font-bold text-green-600 mb-2">67%</div>
              <div className="text-gray-700 font-medium mb-2">Mindre stress</div>
              <div className="text-sm text-gray-600">Psykologisk trygghet minskar stress och förebygger utbrändhet</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center p-6 bg-white rounded-2xl shadow-lg"
            >
              <div className="text-4xl font-bold text-purple-600 mb-2">3x</div>
              <div className="text-gray-700 font-medium mb-2">Mer innovation</div>
              <div className="text-sm text-gray-600">Teams med psykologisk trygghet är tre gånger mer innovativa</div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;